import React from "react";
import { graphql, useFragment } from "react-relay";

import { faHubspot } from "@awesome.me/kit-4c83d50d0d/icons/classic/brands";
import {
	faBookmark,
	faCheckSquare,
	faCommentAltLines,
	faSpinnerThird,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faPrivate, faSalesforceOutline } from "@awesome.me/kit-4c83d50d0d/icons/kit/custom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useSingleton } from "@tippyjs/react";
import cx from "classnames";

import { dispatchAnalyticsTrack } from "~/lib/segment";
import { ActionItemsPanel } from "~/meetings/SeriesSidePanel/ActionItems/ActionItemsPanel";
import Bookmarks from "~/meetings/SeriesSidePanel/Bookmarks/Bookmarks";
import { CommentsPanel } from "~/meetings/SeriesSidePanel/Comments/CommentsPanel";
import { SalesforcePanel } from "~/meetings/SeriesSidePanel/CRMPanel/Salesforce/SalesforcePanel";
import PrivateNotes from "~/meetings/SeriesSidePanel/PrivateNotes/PrivateNotesPanel";
import { eventEmitter } from "~/stream/Comments/events";
import Hubspot from "~/stream/SideBar/CRMPanel/Hubspot";
import { StreamSideBarContext } from "~/stream/SideBar/SideBarContext";
import Tooltip from "~/ui/Tooltip";

import { SidePanelItem } from "./SidePanelItem";

import type { SeriesSidePanel_allowedTabs_stream$key } from "~/__generated__/SeriesSidePanel_allowedTabs_stream.graphql";
import type { SeriesSidePanel_allowedTabs_viewer$key } from "~/__generated__/SeriesSidePanel_allowedTabs_viewer.graphql";
import type { SeriesSidePanel_stream$key } from "~/__generated__/SeriesSidePanel_stream.graphql";

import styles from "./SeriesSidePanel.module.css";

export type TabKey =
	| "private-notes"
	| "action-items"
	| "automations"
	| "bookmarks"
	| "hubspot"
	| "salesforce"
	| "comments";

const Tab = React.memo(function Tab({
	tab,
	setTab,
	label,
	icon,
	currentTab,
	tooltipSingleton,
}: {
	tab: TabKey;
	setTab: (tab: TabKey) => void;
	label: string;
	icon: React.ReactNode;
	currentTab: TabKey | null;
	tooltipSingleton: ReturnType<typeof useSingleton>[1];
}) {
	const sideBarOpen = currentTab !== null;
	const isCurrent = currentTab === tab && sideBarOpen;

	return (
		<Tooltip singleton={tooltipSingleton} content={label} disabled={isCurrent}>
			<button
				className={cx(styles.tab, {
					[styles.current]: isCurrent,
					[styles.sideBarOpen]: sideBarOpen,
				})}
				onClick={() => {
					setTab(tab);
				}}
			>
				{icon}
			</button>
		</Tooltip>
	);
});

export function useAllowedSeriesSidePanelTabs(
	viewer: SeriesSidePanel_allowedTabs_viewer$key,
	stream: SeriesSidePanel_allowedTabs_stream$key | null,
): {
	hubspot: boolean;
	salesforce: boolean;
	salesforceEnhancedNotesEnabled: boolean;
} {
	const viewerData = useFragment(
		graphql`
			fragment SeriesSidePanel_allowedTabs_viewer on Viewer {
				integrations {
					hubspot {
						isConnected
					}
					salesforce {
						isConnected
						enhancedNotesEnabled
					}
				}
				account {
					emailDomainList
					integrations {
						hubspot {
							userConnected
						}
					}
					allowAiFeatures
					billingAccount {
						hasAccessToHubspotFeature: hasAccessToFeature(feature: hubspot_integration)
						hasAccessToSalesforceFeature: hasAccessToFeature(feature: salesforce_integration)
					}
				}
			}
		`,
		viewer,
	);
	const streamData = useFragment(
		graphql`
			fragment SeriesSidePanel_allowedTabs_stream on NoteStream {
				isExternal
				notestreamusers {
					user {
						email
					}
				}
				claimedEvents {
					attendees {
						email
					}
				}
				noteWithRecordingExists
				askCopilotConversationExists
			}
		`,
		stream,
	);

	const isHubspotConnected =
		Boolean(viewerData.integrations.hubspot?.isConnected) ||
		Boolean(viewerData.account.integrations?.hubspot?.userConnected);
	const { hasAccessToHubspotFeature } = viewerData.account.billingAccount;

	const isSalesforceConnected = Boolean(viewerData.integrations.salesforce?.isConnected);
	const { hasAccessToSalesforceFeature } = viewerData.account.billingAccount;
	const salesforceEnhancedNotesEnabled = Boolean(viewerData.integrations.salesforce?.enhancedNotesEnabled);

	const crmAvailableForStream = React.useMemo(() => {
		const isInternal = (email: string) => {
			return viewerData.account.emailDomainList.some(domain => email.endsWith(`@${domain}`));
		};
		const hasExternalUser = streamData?.notestreamusers?.some(noteuser => !isInternal(noteuser.user.email));

		return (
			(hasExternalUser ||
				streamData?.claimedEvents?.some(event =>
					event.attendees.some(attendee => attendee && !isInternal(attendee.email)),
				)) ??
			false
		);
	}, [viewerData.account.emailDomainList, streamData]);

	const hubspot =
		hasAccessToHubspotFeature && streamData?.isExternal === false && isHubspotConnected && crmAvailableForStream;
	const salesforce =
		hasAccessToSalesforceFeature && streamData?.isExternal === false && isSalesforceConnected && crmAvailableForStream;

	return React.useMemo(
		() => ({
			hubspot,
			salesforce,
			salesforceEnhancedNotesEnabled,
		}),
		[hubspot, salesforce, salesforceEnhancedNotesEnabled],
	);
}

const TabBar = React.memo(function TabBar({
	tab,
	setTab,
	showCommentsTab,
	allowedTabs,
}: {
	tab: TabKey | null;
	setTab: (tab: TabKey | null) => void;
	showCommentsTab: boolean;
	allowedTabs: ReturnType<typeof useAllowedSeriesSidePanelTabs>;
}) {
	const [source, target] = useSingleton();

	return (
		<div data-open={tab !== null} className={styles.tabBar}>
			<Tooltip singleton={source} placement="left" />
			<Tab
				tab="private-notes"
				setTab={setTab}
				label="Private Notes"
				icon={<FontAwesomeIcon icon={faPrivate} />}
				currentTab={tab}
				tooltipSingleton={target}
			/>
			<Tab
				tab="action-items"
				setTab={setTab}
				label="Action Items"
				icon={<FontAwesomeIcon icon={faCheckSquare} />}
				currentTab={tab}
				tooltipSingleton={target}
			/>
			<Tab
				tab="bookmarks"
				setTab={setTab}
				label="Bookmarks"
				icon={<FontAwesomeIcon icon={faBookmark} />}
				currentTab={tab}
				tooltipSingleton={target}
			/>
			{allowedTabs.salesforce && (
				<Tab
					tab="salesforce"
					setTab={setTab}
					label="Salesforce"
					icon={<FontAwesomeIcon icon={faSalesforceOutline} />}
					currentTab={tab}
					tooltipSingleton={target}
				/>
			)}
			{allowedTabs.hubspot && (
				<Tab
					tab="hubspot"
					setTab={setTab}
					label="HubSpot"
					icon={<FontAwesomeIcon icon={faHubspot} />}
					currentTab={tab}
					tooltipSingleton={target}
				/>
			)}
			{showCommentsTab && (
				<Tab
					tab="comments"
					setTab={setTab}
					label="Comments"
					icon={<FontAwesomeIcon icon={faCommentAltLines} />}
					currentTab={tab}
					tooltipSingleton={target}
				/>
			)}
		</div>
	);
});

interface SeriesSidePanelProps {
	stream: SeriesSidePanel_stream$key;
	tab: TabKey | null;
	setTab: (tab: TabKey | null) => void;
	allowedTabs: ReturnType<typeof useAllowedSeriesSidePanelTabs>;
}

const SeriesSidePanel = React.memo(function SeriesSidePanel({
	stream: streamRef,
	tab,
	setTab,
	allowedTabs,
}: SeriesSidePanelProps) {
	const stream = useFragment(
		graphql`
			fragment SeriesSidePanel_stream on NoteStream {
				id
				...SeriesSidePanel_allowedTabs_stream
				...Bookmarks_stream
			}
		`,
		streamRef,
	);
	const { currentNoteId, commentContext, setCommentContext } = React.useContext(StreamSideBarContext);

	const onCloseCommentsTab = React.useCallback(() => {
		if (commentContext) {
			eventEmitter.emit("NoteComments.close", commentContext.noteId, commentContext.blockId);
		}
	}, [commentContext]);

	const changeTab = React.useCallback(
		(newTab: TabKey | null) => {
			if (newTab === tab) {
				setTab(null);
				return;
			}
			if (tab === "comments") onCloseCommentsTab();
			if (newTab !== "comments") {
				setCommentContext(undefined);
			}
			setTab(newTab);
			if (!newTab) {
				dispatchAnalyticsTrack("Collapsed stream's side bar", { feature: "side-bar" });
			} else {
				dispatchAnalyticsTrack("Changed tab in the stream's side bar", {
					feature: "side-bar",
					tab: newTab,
					// We are expanding the side bar if the current tab is null
					expanding: !Boolean(tab),
				});
			}
		},
		[tab, setTab, setCommentContext, onCloseCommentsTab],
	);

	React.useEffect(() => {
		if (!commentContext) return;
		setTab("comments");
	}, [commentContext, setTab]);

	const isOpen = tab !== null;

	const onClose = React.useCallback(() => changeTab(null), [changeTab]);

	return (
		<div
			aria-expanded={isOpen}
			className={cx(styles.root, {
				[styles.open]: isOpen,
			})}
		>
			<TabBar tab={tab} setTab={changeTab} showCommentsTab={Boolean(commentContext)} allowedTabs={allowedTabs} />
			<div className={styles.content}>
				<React.Suspense
					fallback={
						<SidePanelItem>
							<SidePanelItem.Body className={styles.loadingBody}>
								<FontAwesomeIcon icon={faSpinnerThird} spin />
							</SidePanelItem.Body>
						</SidePanelItem>
					}
				>
					{isOpen &&
						(tab === "private-notes" ? (
							<PrivateNotes streamId={stream.id} noteId={currentNoteId} onClose={onClose} />
						) : tab === "action-items" ? (
							<ActionItemsPanel streamId={stream.id} onClose={onClose} />
						) : tab === "bookmarks" ? (
							<Bookmarks stream={stream} onClose={onClose} />
						) : tab === "hubspot" && currentNoteId ? (
							<Hubspot streamId={stream.id} noteId={currentNoteId} hasAccessToHubspotIntegration={true} />
						) : tab === "salesforce" && currentNoteId ? (
							<SalesforcePanel
								streamId={stream.id}
								noteId={currentNoteId}
								hasAccessToSalesforceIntegration={true}
								enhancedNotesEnabled={allowedTabs.salesforceEnhancedNotesEnabled}
								onClose={onClose}
							/>
						) : tab === "comments" && commentContext ? (
							<CommentsPanel threadKey={commentContext.threadKey} noteId={commentContext.noteId} onClose={onClose} />
						) : null)}
				</React.Suspense>
			</div>
		</div>
	);
});

export default SeriesSidePanel;
